/*
 * sidebar (config)
 */
import type { AuthUser, UserRole } from "@emr/types"
import { getUserRole } from "@emr/utils"
import type { ClientStatus } from "@emr/db/client"
import { CLIENT_STATUSES } from "@emr/db/client"
import {
	Calendar,
	Clock,
	Factory,
	FileText,
	Info,
	LayoutDashboard,
	Scale,
	Shield,
	SquareTerminal,
	User as UserIcon,
	Users,
} from "lucide-react"

const STATUS_SUBMENU_LABELS: Record<ClientStatus | "none", string> = {
	none: "No Status",
	review_required: "Review Required",
	education_required: "Education Required",
	education_scheduled: "Education Scheduled",
	prescription_required: "Prescription Required",
	prescription_scheduled: "Prescription Scheduled",
	active: "Active",
	sla_required: "SLA Required",
	sla_scheduled: "SLA Scheduled",
	renewal_required: "Renewal Required",
	not_active: "Not Active",
}

export type SidebarFlags = {
	pendingPatientCount?: number
	statusCounts?: Partial<Record<ClientStatus | "none", number>>
}

export function getSidebarMenuList(
	user?: AuthUser | null,
	devMode?: boolean,
	flags?: SidebarFlags,
): any[] {
	const role = getUserRole(user)

	const dashboardGroup = {
		groupLabel: "",
		menus: [
			{
				to: "/dashboard",
				label: "Dashboard",
				icon: LayoutDashboard,
			},
		],
	}

	const menuList = [
		dashboardGroup,
		getMainMenuList(role, flags),
		getBottomMenuList(user, role, devMode),
	].filter((group) => group && group.menus.length > 0)

	return menuList
}

/*
 * main
 */
function getMainMenuList(role: UserRole | null, flags?: SidebarFlags) {
	const menus: any[] = []

	if (role === "patient") {
		menus.push({
			label: "My Profile",
			icon: UserIcon,
			to: "/patient/me",
		})
		menus.push({
			label: "Appointments",
			icon: Calendar,
			to: "/appointment",
		})
		menus.push({
			label: "Health Records",
			icon: FileText,
			to: "/health-record",
		})
	} else if (
		role === "practitioner" ||
		role === "nurse" ||
		role === "sla_specialist" ||
		role === "admin" ||
		role === "super_admin"
	) {
		if (role === "practitioner" || role === "nurse" || role === "sla_specialist") {
			menus.push({
				label: "My Profile",
				icon: UserIcon,
				to: "/auth/practitioner-profile",
			})
		}

		// Build status submenus — "All" always first, then only statuses with patients
		const counts = flags?.statusCounts ?? {}
		const statusSubmenus: Array<{ to: string; label: string }> = [
			{ to: "/patient", label: "All Patients" },
		]
		for (const status of CLIENT_STATUSES) {
			const count = counts[status] ?? 0
			if (count > 0) {
				statusSubmenus.push({
					to: `/patient?status=${status}`,
					label: `${STATUS_SUBMENU_LABELS[status]} (${count})`,
				})
			}
		}
		const noneCount = counts.none ?? 0
		if (noneCount > 0) {
			statusSubmenus.push({
				to: `/patient?status=none`,
				label: `No Status (${noneCount})`,
			})
		}

		menus.push({
			label: "Patients",
			icon: Users,
			to: "/patient",
			submenus: statusSubmenus,
		})

		menus.push({
			label: "Pending Patients",
			icon: Clock,
			to: "/auth/pending-patients",
			badge: flags?.pendingPatientCount,
		})
		menus.push({
			label: "Schedule",
			icon: Calendar,
			to: "/schedule",
		})
		menus.push({
			label: "Appointments",
			icon: Calendar,
			to: "/appointment",
		})
		menus.push({
			label: "Producers",
			icon: Factory,
			to: "/producer",
		})
	}

	if (menus.length === 0) return null

	return { groupLabel: "Services", menus }
}

/*
 * bottom
 */
function getBottomMenuList(
	user?: AuthUser | null,
	role?: UserRole | null,
	devMode?: boolean,
) {
	const settings: any = { groupLabel: "Settings", menus: [] }

	if (user) {
		settings.menus.push({
			to: "/auth/account",
			label: "Account",
			icon: UserIcon,
		})

		if (role === "admin" || role === "super_admin") {
			settings.menus.push({
				label: "Admin",
				icon: Shield,
				to: "/auth/admin",
				submenus: [
					{ to: "/auth/admin", label: "Users" },
					{ to: "/auth/practitioners", label: "Practitioners" },
					{ to: "/auth/attachment-types", label: "Attachment Types" },
				{ to: "/auth/diagnoses", label: "Diagnoses" },
					{ to: "/fax-directory", label: "Fax Directory" },
					{ to: "/referral", label: "Referrals" },
				],
			})
		}
	}

	if (devMode) {
		settings.menus.push({
			to: "/dev",
			label: "Dev",
			icon: SquareTerminal,
		})
	}

	settings.menus.push({
		to: "/legal",
		label: "Legal",
		icon: Scale,
	})

	settings.menus.push({
		to: "/about",
		label: "About",
		icon: Info,
	})

	return settings
}
